import { PencilSimpleIcon } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type JSX } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { PermissionPicker } from '@/components/roles/PermissionPicker';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { toast } from '@/lib/toast';
import { getPermissionsRequest } from '@/services/permissions';
import { deactivateRoleRequest, getRoleRequest, reactivateRoleRequest } from '@/services/roles';
import type { PermissionsResponse } from '@/types/permissions';
import type { RoleDetail } from '@/types/roles';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function RoleDetailPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const { permissions } = useAuth();
  const queryClient = useQueryClient();

  const detailQuery = useQuery<RoleDetail, ApiError>({
    queryKey: ['role', id],
    queryFn: () => getRoleRequest(id),
    enabled: Number.isFinite(id) && id > 0,
  });

  const permissionsQuery = useQuery<PermissionsResponse>({
    queryKey: ['permissions'],
    queryFn: getPermissionsRequest,
  });

  // Plan 07-03 (Rule 3 deviation): the E2E happy path demands deactivate/reactivate
  // buttons on the role detail page. Plan 07-02 wired the service wrappers
  // (deactivateRoleRequest / reactivateRoleRequest) but omitted the UI controls;
  // we add them here so the Phase 7 surface is complete end-to-end.
  const deactivateMutation = useMutation<undefined, ApiError, { id: number }>({
    mutationFn: async ({ id: roleId }) => {
      await deactivateRoleRequest(roleId);
      return undefined;
    },
    onSuccess: () => {
      toast.success('Role deactivated.');
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      void queryClient.invalidateQueries({ queryKey: ['role', id] });
    },
    onError: (err) => {
      toast.error('Could not deactivate role', { description: err.detail ?? err.message });
    },
  });

  const reactivateMutation = useMutation<undefined, ApiError, { id: number }>({
    mutationFn: async ({ id: roleId }) => {
      await reactivateRoleRequest(roleId);
      return undefined;
    },
    onSuccess: () => {
      toast.success('Role reactivated.');
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      void queryClient.invalidateQueries({ queryKey: ['role', id] });
    },
    onError: (err) => {
      toast.error('Could not reactivate role', { description: err.detail ?? err.message });
    },
  });

  if (!permissions.includes('roles.read')) {
    return <Navigate to="/dashboard" replace />;
  }
  if (!Number.isFinite(id) || id <= 0) {
    return <Navigate to="/roles" replace />;
  }

  const canEdit = permissions.includes('roles.update');
  const canDeactivate = permissions.includes('roles.delete');
  const canReactivate = permissions.includes('roles.update');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{detailQuery.data?.name ?? 'Role'}</h1>
          <p className="text-muted-foreground">
            Read-only view of the role and its permission assignments.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && detailQuery.data && (
            <Button asChild data-testid="role-detail-edit-button">
              <Link to={`/roles/${String(id)}/edit`}>
                <PencilSimpleIcon size={16} weight="bold" /> Edit
              </Link>
            </Button>
          )}
          {canDeactivate && detailQuery.data?.isActive === true && (
            <Button
              type="button"
              variant="destructive"
              data-testid="role-detail-deactivate"
              disabled={deactivateMutation.isPending}
              onClick={() => {
                deactivateMutation.mutate({ id });
              }}
            >
              {deactivateMutation.isPending ? 'Deactivating…' : 'Deactivate'}
            </Button>
          )}
          {canReactivate && detailQuery.data?.isActive === false && (
            <Button
              type="button"
              variant="default"
              data-testid="role-detail-reactivate"
              disabled={reactivateMutation.isPending}
              onClick={() => {
                reactivateMutation.mutate({ id });
              }}
            >
              {reactivateMutation.isPending ? 'Reactivating…' : 'Reactivate'}
            </Button>
          )}
        </div>
      </div>

      {detailQuery.isLoading && (
        <p className="text-sm text-muted-foreground" data-testid="role-detail-loading">
          Loading role…
        </p>
      )}
      {detailQuery.isError && (
        <p className="text-sm text-destructive" data-testid="role-detail-error">
          Failed to load role.
        </p>
      )}

      {detailQuery.data && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Overview</CardTitle>
              <CardDescription data-testid="role-detail-description">
                {detailQuery.data.description ?? '—'}
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
              <div>
                <div className="text-muted-foreground text-xs">Status</div>
                <Badge
                  variant={detailQuery.data.isActive ? 'default' : 'secondary'}
                  data-testid="role-detail-status"
                >
                  {detailQuery.data.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Users</div>
                <div className="font-medium" data-testid="role-detail-users-count">
                  {detailQuery.data.usersCount}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Created</div>
                <div className="font-mono text-xs">{formatDate(detailQuery.data.createdAt)}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Updated</div>
                <div className="font-mono text-xs">{formatDate(detailQuery.data.updatedAt)}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Permissions</CardTitle>
              <CardDescription>
                Grouped by module. Use the Edit button to modify the assignment.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {permissionsQuery.isLoading && (
                <p className="text-sm text-muted-foreground">Loading permissions registry…</p>
              )}
              {permissionsQuery.data && (
                <PermissionPicker
                  value={detailQuery.data.permissionCodes}
                  permissions={permissionsQuery.data}
                  readOnly
                />
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
